import type { WalletSource } from "../../analytics/walletEvents";
import { useSimMode } from "../SimModeContext";

/**
 * Subset of the apps/main `useWalletModal` return shape that widgets
 * actually destructure. Kept loose so shared doesn't couple to the
 * wallet module's full API; apps/main re-types the injected real hook
 * precisely via module augmentation.
 */
interface SimWalletModalApi {
    openModal: (source?: WalletSource) => void;
}

/**
 * In sim mode the wallet is faked as connected, so the disconnected
 * "Connect Wallet" CTA — the only caller of `openModal` — never fires.
 * The no-op keeps the destructure valid without doing anything.
 */
const SIM_WALLET_MODAL: SimWalletModalApi = {
    openModal: (): void => {}
};

/**
 * Resolver hook for the widget-side wallet-connect modal.
 *
 * - External (sim-only) consumers: no `realHooks.useWalletModal` is
 *   injected, so this returns the no-op `openModal`.
 * - apps/main (via `SimRealHooksProvider`): calls the injected real
 *   `useWalletModal`, preserving production connect-modal behavior.
 *
 * Crucially, **this file has no static import of `../wallet`**. That
 * barrel is the sole carrier of `@solana/connector`,
 * `@tanstack/react-query`, `@solomon-apps/config`, and
 * `@solomon-labs/bigdecimal` into the widget tree — none of which the
 * simulated path needs. Routing the modal through this orchestrator
 * keeps all of them out of the sim bundle.
 */
export function useWalletModalOrchestration<TReturn = SimWalletModalApi>(): TReturn {
    const sim = useSimMode();
    const useReal = (sim.realHooks as Record<string, unknown> | undefined)?.["useWalletModal"] as
        | (() => TReturn)
        | undefined;
    const useFallback = (): TReturn => SIM_WALLET_MODAL as unknown as TReturn;
    const useImplementation = useReal ?? useFallback;
    return useImplementation();
}
